import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiClient } from '../../services/api';
import EmptyState from '../shared/EmptyState';
import './TestingActivitySummary.css';

interface ActivityRow {
  sort_key: string;
  tc_created?: number;
  tc_executed?: number;
  sc_created?: number;
  sc_executed?: number;
}

// A % change is only meaningful when there was something on both sides to
// compare — 0 this period ("down 100%") or 0 last period ("up 100%") are
// both just artifacts of dividing by/into zero, not a real percentage.
const pctDelta = (curr: number, prev: number): number | 'new' | null => {
  if (curr === 0) return null;
  if (prev === 0) return 'new';
  return Math.round(((curr - prev) / prev) * 1000) / 10;
};

const DeltaTag: React.FC<{ delta: number | 'new' | null }> = ({ delta }) => {
  if (delta === null) return null;
  if (delta === 'new') return <span className="tas-delta up">▲ New</span>;
  const up = delta >= 0;
  return <span className={`tas-delta ${up ? 'up' : 'down'}`}>{up ? '▲' : '▼'} {Math.abs(delta)}%</span>;
};

export const TestingActivitySummary: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<ActivityRow[]>([]);

  useEffect(() => {
    let cancelled = false;
    apiClient.get('/api/v1/dashboard/activity-report?period=monthly&view=overall&months_back=2')
      .then(resp => { if (!cancelled) setRows((resp.data as any).rows || []); })
      .catch(() => { if (!cancelled) setRows([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const sorted = [...rows].sort((a, b) => a.sort_key.localeCompare(b.sort_key));
  const current = sorted[sorted.length - 1];
  const prior = sorted[sorted.length - 2];

  const items = current ? [
    { label: 'Test Cases Created', curr: current.tc_created ?? 0, prev: prior?.tc_created ?? 0 },
    { label: 'Test Cases Executed', curr: current.tc_executed ?? 0, prev: prior?.tc_executed ?? 0 },
    { label: 'Scenarios Created', curr: current.sc_created ?? 0, prev: prior?.sc_created ?? 0 },
    { label: 'Scenarios Executed', curr: current.sc_executed ?? 0, prev: prior?.sc_executed ?? 0 },
  ].filter(row => row.curr > 0) : [];

  return (
    <div className="tas-card">
      <div className="tas-header"><h3>Testing Activity</h3></div>
      {loading ? (
        <div className="tas-skeleton" />
      ) : items.length === 0 ? (
        <EmptyState message="No activity yet this period." compact />
      ) : (
        <ul className="tas-list">
          {items.map(row => (
            <li key={row.label} className="tas-row">
              <span className="tas-label">{row.label}</span>
              <span className="tas-value">{row.curr.toLocaleString()}</span>
              <DeltaTag delta={prior ? pctDelta(row.curr, row.prev) : null} />
            </li>
          ))}
        </ul>
      )}
      <div className="tas-footer">
        <Link to="/reports?tab=activity">View Detailed Report →</Link>
      </div>
    </div>
  );
};

export default TestingActivitySummary;
